// Feature-list primitiva (doutrina Anthropic "effective harnesses"):
// a lista nasce com TUDO 'failing'; o agente SÓ muda status (com prova para
// 'passing') — nunca adiciona, remove ou edita itens depois de criada.
// FONTE ÚNICA usada pelo renderer (apolo) e pelo Worker (apolo-api).

const FEATURE_STATUSES = ['failing', 'passing', 'blocked'];
export const FEATURE_LIST_PATH = '.apolo/features.json';
const MAX_FEATURES = 50;

/** @typedef {{id: string, title: string, acceptance: string, status: string, proof?: string}} FeatureItem */

function fail(msg) { throw new Error(`feature-list: ${msg}`); }

/** @param {any} raw @param {number} idx */
function validateItem(raw, idx) {
  if (!raw || typeof raw !== 'object') fail(`item ${idx} não é um objeto`);
  const id = typeof raw.id === 'string' ? raw.id.trim() : '';
  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  const acceptance = typeof raw.acceptance === 'string' ? raw.acceptance.trim() : '';
  if (!id) fail(`item ${idx} sem "id" (string curta única, ex: "f1-login")`);
  if (id.length > 64) fail(`id "${id.slice(0, 20)}…" excede 64 chars`);
  if (!title) fail(`item "${id}" sem "title"`);
  if (title.length > 300) fail(`title de "${id}" excede 300 chars`);
  if (!acceptance) fail(`item "${id}" sem "acceptance" (critério VERIFICÁVEL de aceitação)`);
  if (acceptance.length > 1000) fail(`acceptance de "${id}" excede 1000 chars`);
  return { id, title, acceptance };
}

/** Cria a lista a partir de itens propostos. Status é SEMPRE forçado a 'failing'. */
export function createFeatureList(items) {
  if (!Array.isArray(items) || items.length === 0) fail('forneça "features" como array não-vazio de {id, title, acceptance}');
  if (items.length > MAX_FEATURES) fail(`máximo ${MAX_FEATURES} itens — decomponha em listas menores por sprint`);
  const seen = new Set();
  const features = items.map((raw, idx) => {
    const it = validateItem(raw, idx);
    if (seen.has(it.id)) fail(`id duplicado: "${it.id}"`);
    seen.add(it.id);
    return { ...it, status: 'failing' };
  });
  return { version: 1, features };
}

/** Parseia e valida um features.json existente. */
export function parseFeatureList(text) {
  let data;
  try { data = JSON.parse(text); } catch { fail('JSON inválido em features.json'); }
  if (!data || !Array.isArray(data.features)) fail('features.json sem array "features"');
  const seen = new Set();
  const features = /** @type {any[]} */ (data.features).map((raw, idx) => {
    const it = validateItem(raw, idx);
    if (seen.has(it.id)) fail(`id duplicado: "${it.id}"`);
    seen.add(it.id);
    const status = FEATURE_STATUSES.includes(raw.status) ? raw.status : 'failing';
    const out = /** @type {FeatureItem} */ ({ ...it, status });
    if (typeof raw.proof === 'string' && raw.proof.trim()) out.proof = raw.proof.trim();
    return out;
  });
  return { version: 1, features };
}

export function serializeFeatureList(list) {
  return JSON.stringify(list, null, 2) + '\n';
}

/**
 * Única mutação permitida pós-criação: mudar o STATUS de um item existente.
 * 'passing' exige prova (o que foi RODADO que comprova). Reconstrói a lista
 * a partir do parse — qualquer outra alteração é estruturalmente impossível.
 * @param {string} text
 * @param {{id?: string, status?: string, proof?: string}} [update]
 */
export function applyStatusUpdate(text, { id, status, proof } = {}) {
  const list = parseFeatureList(text);
  const wantedId = typeof id === 'string' ? id.trim() : '';
  if (!wantedId) fail('informe o "id" do item');
  if (!FEATURE_STATUSES.includes(status)) fail(`status inválido "${status}" — use ${FEATURE_STATUSES.join('|')}`);
  const item = list.features.find((f) => f.id === wantedId);
  if (!item) fail(`id "${wantedId}" não existe. Ids válidos: ${list.features.map((f) => f.id).join(', ')}. A lista é IMUTÁVEL — não é possível adicionar itens.`);
  const cleanProof = typeof proof === 'string' ? proof.trim() : '';
  if (status === 'passing' && cleanProof.length < 12) fail('"passing" exige "proof": descreva a verificação RODADA (comando + resultado), mínimo 12 chars');
  item.status = status;
  if (cleanProof) item.proof = cleanProof.slice(0, 500);
  else delete item.proof;
  return list;
}

/** Contagens para done-gate / progress file. */
export function summarizeFeatures(list) {
  const counts = { failing: 0, passing: 0, blocked: 0 };
  for (const f of list.features) counts[f.status] += 1;
  return { ...counts, total: list.features.length };
}
